import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { ChannelMark } from "../../components/channel/ChannelMark.js";
import { AppNotice } from "../../components/common/AppNotice.js";
import { IsolatedBrands } from "../../components/common/IsolatedBrands.js";
import { ProductState } from "../../components/common/ProductState/index.js";
import { cn } from "../../lib/utils.js";

const spinClass = "animate-[spin_0.8s_linear_infinite] motion-reduce:animate-none";

export function WhatsAppConnectingPanel({
  title,
  description,
  note,
}: {
  title: string;
  description: string;
  note?: string;
}): React.JSX.Element {
  return (
    <div
      className="mx-auto grid w-full max-w-md justify-items-center gap-3 rounded-xl border border-line bg-surface px-6 py-8 text-center shadow-sm"
      data-testid="whatsapp-connecting"
      role="status"
      aria-busy="true"
    >
      <ChannelMark platformType="WHATSAPP" size="list" />
      <div className="grid max-w-sm gap-1.5">
        <h2 className="m-0 text-[1.05rem] font-semibold tracking-[-0.01em] text-ink">
          <IsolatedBrands text={title} />
        </h2>
        <p className="m-0 text-[0.8125rem] leading-relaxed text-muted">{description}</p>
      </div>
      <span
        className={cn("my-1 size-8 rounded-full border-2 border-whatsapp-muted border-t-whatsapp", spinClass)}
        aria-hidden="true"
      />
      {note ? (
        <p className="m-0 max-w-sm text-[0.75rem] leading-relaxed text-ink-muted">
          <IsolatedBrands text={note} />
        </p>
      ) : null}
    </div>
  );
}

/** Full channel area while Embedded Signup completes. Replaces the channel detail frame. */
export function WhatsAppConnectingScreen(props: {
  title: string;
  description: string;
  note?: string;
}): React.JSX.Element {
  return (
    <div
      className="h-full min-h-0 overflow-y-auto px-4 pt-16 pb-10 md:px-8 md:pt-24"
      data-testid="channels-whatsapp-connecting"
    >
      <WhatsAppConnectingPanel {...props} />
    </div>
  );
}

export function WhatsAppAccountGate({
  testId,
  title,
  description,
  benefits,
  action,
  readonly,
  notice,
}: {
  testId: string;
  title: string;
  description: string;
  benefits?: readonly string[];
  action?: ReactNode;
  readonly?: string | null;
  notice?: string | null;
}): React.JSX.Element {
  return (
    <div className="mx-auto grid w-full max-w-md gap-4">
      <ProductState
        tone="empty"
        testId={testId}
        visual={<ChannelMark platformType="WHATSAPP" size="list" />}
        title={title}
        description={description}
        action={action}
      />
      {benefits && benefits.length > 0 ? (
        <ul className="m-0 grid list-none gap-2 px-2">
          {benefits.map((benefit) => (
            <li key={benefit} className="flex items-start gap-2 text-start text-[0.8125rem] text-muted">
              <Check size={15} className="mt-0.5 shrink-0 text-whatsapp" aria-hidden="true" />
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {readonly ? <p className="m-0 text-center text-[0.8125rem] text-muted">{readonly}</p> : null}
      {notice ? <AppNotice severity="error" description={notice} testId={`${testId}-notice`} /> : null}
    </div>
  );
}

export function WhatsAppTemplatesGate({
  title,
  description,
  action,
  readonly,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  readonly?: string | null;
}): React.JSX.Element {
  return (
    <ProductState
      tone="empty"
      testId="whatsapp-templates-disconnected"
      visual={<ChannelMark platformType="WHATSAPP" size="list" />}
      title={title}
      description={description}
      action={
        readonly ? <p className="m-0 text-[0.8125rem] text-muted">{readonly}</p> : action
      }
    />
  );
}
